import type { Cadence } from './reportEnums.constants';

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * @param monthKey - `YYYY-MM` month key.
 * @returns Short label such as `Jun 2026`.
 */
export const formatReportMonthLabel = (monthKey: string): string => {
  const [year, month] = monthKey.split('-');
  const monthIndex = Number.parseInt(month ?? '', 10) - 1;
  if (!year || monthIndex < 0 || monthIndex > 11) {
    return monthKey;
  }
  return `${MONTH_NAMES[monthIndex]} ${year}`;
};

/**
 * @param endMonthKey - `YYYY-MM` end month of a calendar quarter.
 * @returns Quarter number 1–4.
 */
const quarterFromEndMonth = (endMonthKey: string): number => {
  const month = Number.parseInt(endMonthKey.split('-')[1] ?? '', 10);
  return Math.ceil(month / 3);
};

/**
 * @param endMonthKey - `YYYY-MM` end month of a calendar quarter.
 * @returns Start month key for that quarter.
 */
const quarterStartMonthKey = (endMonthKey: string): string => {
  const [year, month] = endMonthKey.split('-');
  const endMonth = Number.parseInt(month ?? '', 10);
  const startMonth = endMonth - 2;
  return `${year}-${String(startMonth).padStart(2, '0')}`;
};

/**
 * Builds the stored `reportingPeriod` label from structured picker values.
 * @param cadence - Report cadence.
 * @param startMonthKey - Start `YYYY-MM` (monthly/quarterly).
 * @param endMonthKey - End `YYYY-MM` (monthly/quarterly).
 * @param measurementPeriod - JOC measurement period label.
 * @returns Composed reporting period string for persistence.
 */
export const composeReportingPeriodLabel = (
  cadence: Cadence,
  startMonthKey: string,
  endMonthKey: string,
  measurementPeriod: string,
): string => {
  if (cadence === 'adhoc') {
    return measurementPeriod.trim();
  }
  if (cadence === 'monthly') {
    const label = formatReportMonthLabel(endMonthKey || startMonthKey);
    if (!startMonthKey || startMonthKey === endMonthKey) {
      return label;
    }
    return `${formatReportMonthLabel(startMonthKey)} – ${label}`;
  }
  const end = endMonthKey || startMonthKey;
  const start = quarterStartMonthKey(end);
  const q = quarterFromEndMonth(end);
  const year = end.split('-')[0];
  return `Q${q} ${year} (${formatReportMonthLabel(start)} – ${formatReportMonthLabel(end)})`;
};

/**
 * @param count - Number of months to include.
 * @returns Recent `YYYY-MM` keys ending at the current month.
 */
export const buildDefaultAvailableMonthKeys = (count = 24): string[] => {
  const keys: string[] = [];
  const cursor = new Date();
  cursor.setDate(1);
  for (let i = 0; i < count; i += 1) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, '0');
    keys.unshift(`${year}-${month}`);
    cursor.setMonth(cursor.getMonth() - 1);
  }
  return keys;
};

/** Default JOC measurement period options when a customer has none configured. */
export const DEFAULT_MEASUREMENT_PERIODS = [
  'CY2025',
  'Rolling 12 months',
  'HEDIS MY2025',
] as const;
